//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { Enums } from '../../../../models/common'
import { ColumnModel } from '../../../api/api.models'
import { GridController, GridOptions, GridTypes } from '../../../common/grid/grid.controller'
import { IReferenceType } from '../../../../models/util/interfaces'
import { OpAlarmDef } from '../../../../models/core/op-alarm-def'
import { OpDataType, OpUnit } from '../../../../models/core'
import { Dictionary,  LocalizationHelper, TypeHelper } from '../../../../helpers'
import { GridService } from '../../../../services/grid'
import { ReferenceTypeService } from '../../../../services/reference-type'
import { UserSessionService } from '../../../../services/user-session'
import { IAlarmDefinitions, AlarmDefinitionsOptions } from './alarm-definitions.interfaces'

export class AlarmDefinitionsController
    implements ng.IComponentController
    , IAlarmDefinitions {
    // #region Consts

    private static get ALARM_DEFINITIONS_GRID_NAME(): string { return 'AlarmDefinitionsGrid' }

    // #endregion 

    // #region Members

    private dataGrid: any
    private grid: GridController<OpAlarmDef>
    private gridOptions: GridOptions
    private options: AlarmDefinitionsOptions

    private currentAlarmDefs: OpAlarmDef[] = []
    private alarmDefinitionsWithTemplates: OpAlarmDef[] = []

    private units: Dictionary<OpUnit>
    private dataTypes: Dictionary<OpDataType>

    private alarmDefColumnsFromGroups: ColumnModel[]

    // #endregion 

    // #region Ctor

    constructor(
        private gridService: GridService,
        private referenceTypeService: ReferenceTypeService,
        private userSessionService: UserSessionService
    ) {
    }

    // #endregion

    // #region ng.IComponentController implementation

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion

    // #region IAlarmDefinitions implementation

    public setDataSource(alarmDefs: OpAlarmDef[]): void {
        this.alarmDefinitionsWithTemplates = alarmDefs
        this.currentAlarmDefs = this.filterOutAlarmDefTemplates(alarmDefs)
        this.grid.setGetDataSource(() => this.currentAlarmDefs)
    }

    public refresh(): JQueryPromise<void> {
        return this.initialize()
    }

    public getCurrentAlarmDefs(): OpAlarmDef[] {
        return this.currentAlarmDefs
    }

    // #endregion

    // #region Setup

    private getAlarmDefinitionsColumns(): ColumnModel[] {
        const containsSelectedFilterOperation: string = 'contains' // https://js.devexpress.com/Documentation/ApiReference/UI_Widgets/dxDataGrid/Configuration/columns/#selectedFilterOperation

        const columns: ColumnModel[] = []

        const isEnabledColumn: ColumnModel = new ColumnModel()
        isEnabledColumn.idName = 'isEnabled'
        isEnabledColumn.name = 'IsEnabled'
        isEnabledColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.IsEnabled)
        isEnabledColumn.type = Enums.DataTypeClass.Boolean

        columns.push(isEnabledColumn)

        const nameColumn: ColumnModel = new ColumnModel()
        nameColumn.idName = 'name'
        nameColumn.name = 'Name'
        nameColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)
        nameColumn.type = Enums.DataTypeClass.Text
        nameColumn.selectedFilterOperation = containsSelectedFilterOperation

        columns.push(nameColumn)

        const valueColumn: ColumnModel = new ColumnModel()
        valueColumn.idName = 'value'
        valueColumn.name = 'Value'
        valueColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Value)
        valueColumn.type = Enums.DataTypeClass.Text
        valueColumn.selectedFilterOperation = containsSelectedFilterOperation
        valueColumn.calculateCellValue = (args: any) => { return this.calculateAlarmDefinitionCellValue(args) }

        columns.push(valueColumn)

        return columns
    }

    private filterOutAlarmDefTemplates(alarmDefs: OpAlarmDef[]): OpAlarmDef[] {
        const alarmDefsWithoutTemplates: OpAlarmDef[] = []

        if (alarmDefs != null && alarmDefs.length > 0) {
            for (let i = 0; i < alarmDefs.length; i++) {
                for (let j = 0; j < alarmDefs.length; j++) {
                    if (alarmDefs[i].name === alarmDefs[j].name && alarmDefs[i].idAlarmDef != 0) {
                        alarmDefsWithoutTemplates.push(alarmDefs[i])
                        break
                    }
                }
            }
        }

        return alarmDefsWithoutTemplates
    }

    private getAlarmDefColumnsFromGroups(): JQueryPromise<ColumnModel[]> {
        return this.gridService
            .getColumnModelByCategory(
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEF_REF_PARAMS,
                Enums.SimaxModuleName.Meter_Alarm, {
                groupReferenceType: Enums.ReferenceType.IdDistributor,
                groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
                skipView: true
            })
    }

    private getAlarmDefinitions(): JQueryPromise<OpAlarmDef[]> {
        return this.userSessionService
            .getDataProvider()
            .AlarmDef
            .getAlarmDef(this.options.referenceType, this.options.referenceObjectId)
    }

    private initialize(): JQueryPromise<void> {
        if (this.options.referenceObjectId != null && this.options.referenceObjectId !== 0) {
            return $.when<any>(
                this.getAlarmDefinitions(),
                this.getAlarmDefColumnsFromGroups(),
                this.setDictionaries()
            )
                .then((alarmDefinitions: OpAlarmDef[], alarmDefColumnsFromGroups: ColumnModel[]) => {

                    alarmDefinitions = alarmDefinitions || []

                    this.alarmDefinitionsWithTemplates = [].concat(alarmDefinitions)
                    this.alarmDefColumnsFromGroups = alarmDefColumnsFromGroups
                    this.currentAlarmDefs = []

                    alarmDefinitions = this.filterOutAlarmDefTemplates(alarmDefinitions)

                    alarmDefinitions.forEach((alarmDef: OpAlarmDef) => {
                        if (alarmDef.idAlarmDef !== 0) {
                            this.currentAlarmDefs.push(alarmDef)
                        }
                    })

                    this.setMainGridOptions()
                    this.onInitialized()
                })

        }
        else {
            this.setMainGridOptions()
            this.onInitialized()
        }

        return $.Deferred<void>().resolve().promise()
    }

    private setMainGridOptions(): void {
        this.gridOptions = {
            name: AlarmDefinitionsController.ALARM_DEFINITIONS_GRID_NAME,
            type: GridTypes.NO_SEARCH_BAR_SMALL,
            ignoreKey: true,
            columnAutoWidth: false,
            addCustomFilters: true,
            dataSource: this.currentAlarmDefs,

            columnsDictionary: this.getAlarmDefinitionsColumns(),

            noDataText: this.options.referenceObjectId === 0 ? LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoAssignedMeter) : null,

            allowColumnReordering: this.options.isColumnReorderAllowed,

            dataSourceParser: (args: any) => { return this.mainGridDataSourceParser(args) },

            buttonsOptions: {
                enabled: false
            },

            masterDetail: {
                enabled: false,
                template: 'detail',
                autoExpandAll: true
            },

            onDataReady: (e: any) => {
                this.dataGrid.repaint()
            },

            onInitialized: (e: any, grid: GridController<any>) => {
                this.grid = grid
                this.dataGrid = e.component
            },

            onContentReady: (e: any) => {
                this.onContentReady()
            }
        }
    }

    private setDictionaries(): JQueryPromise<void> {
        return this.userSessionService
            .getDataProvider()
            .System
            .getDictionaries([Enums.ReferenceType.IdUnit, Enums.ReferenceType.IdDataType])
            .then((dictionaries: Dictionary<Dictionary<IReferenceType>>) => {
                this.units = this.referenceTypeService.extractReferenceTypeDictionary(dictionaries, Enums.ReferenceType.IdUnit, (unit: OpUnit) => unit.idUnit)
                this.dataTypes = this.referenceTypeService.extractReferenceTypeDictionary(dictionaries, Enums.ReferenceType.IdDataType, (unit: OpDataType) => unit.idDataType)
            })
    }

    // #endregion

    // #region Events

    private onInitialized(): void {
        if (this.options != null && TypeHelper.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    private onContentReady(): void {
        if (this.options != null && TypeHelper.isFunction(this.options.onLoaded)) {
            this.options.onLoaded(this, this.currentAlarmDefs, this.alarmDefinitionsWithTemplates)
        }
    }

    // #endregion

    // #region AlarmDefinitionsController

    private getAlarmDefFromGrid(args: any): OpAlarmDef {
        if (args == null) {
            return null
        }

        return _.has(args, 'idAlarmDef')
            ? <OpAlarmDef>args
            : <OpAlarmDef>this.gridService.getSourceObjectFromGridObject(args, Enums.ReferenceType.IdAlarmDef)
    }

    private getUnitByIdDataType(idDataType: number): OpUnit {
        if (this.dataTypes != null) {
            const dataType: OpDataType = this.dataTypes.tryGetValue(idDataType)

            if (dataType != null) {
                return this.units.tryGetValue(dataType.idUnit)
            }
        }

        return null
    }

    private mainGridDataSourceParser(args: any): {
        sourceObject?: any
        additionalFields?: { key: string, value: any }[]
    } {
        const alarmDef: OpAlarmDef = this.getAlarmDefFromGrid(args)

        if (alarmDef == null) {
            return null
        }

        const result: {
            sourceObject?: any
            additionalFields?: { key: string, value: any }[]
        } = {}

        result.sourceObject = args
        result.additionalFields = [
            {
                key: 'isEnabled',
                value: alarmDef.isEnabled
            },
            {
                key: 'name',
                value: alarmDef.name
            },
            {
                key: 'currentAlarmDefGroups',
                value: alarmDef.currentAlarmDefGroups
            }
        ]

        return result
    }

    private calculateAlarmDefinitionCellValue(data: any): string {
        if (data == null) {
            return TypeHelper.emptyString
        }

        const alarmDef: OpAlarmDef = this.getAlarmDefFromGrid(data)

        if (!(alarmDef instanceof OpAlarmDef)) {
            return TypeHelper.emptyString
        }

        if (!alarmDef.visible) {
            return TypeHelper.emptyString
        }

        if (alarmDef.systemAlarmValue != null) {
            const systemAlarmValueText: string = _.isBoolean(alarmDef.systemAlarmValue)
                ? LocalizationHelper.getLocalizedText(alarmDef.systemAlarmValue.toString())
                : alarmDef.systemAlarmValue

            const unit: OpUnit = this.getUnitByIdDataType(alarmDef.idDataTypeAlarm)
            let valueText: string = systemAlarmValueText

            if (unit != null) {
                valueText += ` ${unit.toString()}`
            }

            return valueText
        }

        const idDataTypeConfigColumn: ColumnModel = _.find(this.alarmDefColumnsFromGroups, (column: ColumnModel) => {
            return column.id === alarmDef.idDataTypeConfig
        })

        if (idDataTypeConfigColumn != null) {
            return idDataTypeConfigColumn.caption
        }

        return TypeHelper.emptyString
    }

    // #endregion
}
